/**
 * The task pocket's rows (spec §6.5, item 5): Status, Assignee and Priority
 * always; Due, Blocked by, Parent, Subtasks and Pull request when set.
 * Values are value menus with today's options and status transitions.
 */
import { useCallback, useRef, useState } from "react";
import { updateTask, type Task } from "@/api/tasks";
import { Icons, PocketRow } from "@/components/ui";
import {
  TASK_PRIORITY_LABEL_KEYS,
  TASK_STATUS_LABEL_KEYS,
} from "@/components/shapes/taskboard-frame/task-metadata-chips";
import {
  activeBlockers,
  taskAssigneeName,
  taskPullRequestText,
} from "@/components/shapes/taskboard-frame/task-state";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { formatDate, translate, useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";
import { useNotificationStore } from "@/stores/notification-store";
import { useAuth } from "@/hooks/use-auth";
import { openExternalUrl } from "@/lib/open-external-url";
import { safeWebUrl } from "@/lib/safe-web-url";
import { useAgentStore } from "@/stores/agent-store";
import { useTasksStore } from "@/stores/tasks-store";
import { PRIORITIES, taskStatusOptions } from "./task-detail-config";
import { TaskValueMenu, type TaskValueMenuOption } from "./task-value-menu";

const NOBODY = "nobody";
const ME = "me";
const AGENT = "agent:";
const ROW_ICON = "h-4 w-4 text-muted";
const LINK_VALUE =
  "flex min-w-0 max-w-[11rem] items-center gap-1 rounded-pill px-2 py-1 text-appkit-body text-muted outline-none transition-colors hover:bg-[var(--color-interactive-hover)] hover:text-primary focus-visible:ring-2 focus-visible:ring-accent";

function formatDue(dueAt: string): string {
  return formatDate(dueAt, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function TaskPocketRows({
  projectId,
  task,
  onOpenTask,
}: {
  projectId: string;
  task: Task;
  /** Opens another task (a blocker) where the pocket sits. */
  onOpenTask: (taskId: string) => void;
}) {
  const t = useT(panelsTasksMessages);
  const tBoard = useT(framesTaskboardMessages);
  const changeTaskStatus = useTasksStore((s) => s.changeTaskStatus);
  const editTask = useTasksStore((s) => s.editTask);
  const canAssignAgent = useAgentStore(
    (state) => state.specialistCreationEnabled,
  );
  const agents = useAgentStore((s) => s.agents);
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const assigningRef = useRef(false);
  const [assigning, setAssigning] = useState(false);
  const reassign = useCallback(
    async (choice: string) => {
      if (assigningRef.current) return;
      assigningRef.current = true;
      setAssigning(true);
      const stillCurrent = captureDesktopChatAuthScopeGuard();
      const payload =
        choice === NOBODY
          ? { assigned_agent: null, assigned_user: null }
          : choice === ME
            ? { assigned_user: userId, assigned_agent: null }
            : {
                assigned_agent: choice.slice(AGENT.length),
                assigned_user: null,
              };
      try {
        const updated = await updateTask(projectId, task.id, payload);
        if (!stillCurrent()) return;
        useTasksStore.setState((state) => ({
          tasks: state.tasks.map((item) =>
            item.id === task.id ? updated : item,
          ),
        }));
      } catch {
        if (stillCurrent())
          useNotificationStore.getState().addSystemToast({
            id: "task-assignee-error",
            title: translate(panelsTasksMessages, "assigneeFailed"),
            level: "error",
          });
      } finally {
        assigningRef.current = false;
        setAssigning(false);
      }
    },
    [projectId, task.id, userId],
  );

  const assigneeOptions: TaskValueMenuOption<string>[] = [
    {
      value: NOBODY,
      label: task.native_subagent ? t("subAgent") : t("nobody"),
    },
    ...(userId
      ? [{ value: ME, label: user?.name || user?.email || t("me") }]
      : []),
    ...(canAssignAgent
      ? agents.map((agent) => ({
          value: `${AGENT}${agent.agent_template}`,
          label: agent.template_name,
        }))
      : []),
  ];
  const assigneeSelected = task.assigned_agent
    ? `${AGENT}${task.assigned_agent}`
    : !task.assigned_user
      ? NOBODY
      : task.assigned_user === userId
        ? ME
        : undefined;
  const priorityOptions = PRIORITIES.map((priority) => ({
    value: priority,
    label: tBoard(TASK_PRIORITY_LABEL_KEYS[priority]),
  }));
  const priorityLabel =
    priorityOptions.find((option) => option.value === task.priority)?.label ??
    task.priority;
  const pullRequestUrl = safeWebUrl(task.pr_url);

  return (
    <div className="mt-2.5" data-testid="task-pocket-rows">
      <PocketRow
        testId="task-pocket-row-status"
        leading={<Icons.CircleDot className={ROW_ICON} aria-hidden="true" />}
        title={t("status")}
        trailing={
          <TaskValueMenu
            label={t("status")}
            value={tBoard(TASK_STATUS_LABEL_KEYS[task.status])}
            options={taskStatusOptions(task.status)}
            onSelect={(status) =>
              void changeTaskStatus(projectId, task.id, status)
            }
            testId="task-pocket-status"
          />
        }
      />
      <PocketRow
        testId="task-pocket-row-assignee"
        leading={<Icons.User className={ROW_ICON} aria-hidden="true" />}
        title={t("assignee")}
        trailing={
          <TaskValueMenu
            disabled={assigning || !!task.direct_session}
            label={t("assignee")}
            value={taskAssigneeName(task) ?? t("nobody")}
            options={assigneeOptions}
            selected={assigneeSelected}
            onSelect={(choice) => void reassign(choice)}
            testId="task-pocket-assignee"
          />
        }
      />
      <PocketRow
        testId="task-pocket-row-priority"
        leading={<Icons.Signal className={ROW_ICON} aria-hidden="true" />}
        title={t("priority")}
        trailing={
          <TaskValueMenu
            label={t("priority")}
            value={priorityLabel}
            options={priorityOptions}
            selected={task.priority}
            onSelect={(priority) =>
              void editTask(projectId, task.id, { priority })
            }
            testId="task-pocket-priority"
          />
        }
      />
      {task.due_at ? (
        <PocketRow
          testId="task-pocket-row-due"
          leading={<Icons.Calendar className={ROW_ICON} aria-hidden="true" />}
          title={t("due")}
          trailing={
            <span
              className="px-2 text-appkit-body text-muted"
              data-testid="task-pocket-due"
            >
              {formatDue(task.due_at)}
            </span>
          }
        />
      ) : null}
      {activeBlockers(task).map((dependency) => (
        <PocketRow
          key={dependency.id}
          testId={`task-pocket-row-blocked-by-${dependency.id}`}
          leading={<Icons.Link className={ROW_ICON} aria-hidden="true" />}
          title={t("blockedBy")}
          trailing={
            <button
              type="button"
              onClick={() => onOpenTask(dependency.id)}
              title={dependency.title}
              className={LINK_VALUE}
              data-testid={`task-pocket-blocked-by-${dependency.id}`}
            >
              <span className="truncate">{dependency.ticket_display}</span>
            </button>
          }
        />
      ))}
      {task.parent_task_summary && (
        <PocketRow
          testId={`task-pocket-row-parent-${task.parent_task_summary.id}`}
          leading={<Icons.ArrowUp className={ROW_ICON} aria-hidden="true" />}
          title={t("parent")}
          trailing={
            <button
              type="button"
              onClick={() => onOpenTask(task.parent_task_summary!.id)}
              title={task.parent_task_summary.title}
              className={LINK_VALUE}
              data-testid={`task-pocket-parent-${task.parent_task_summary.id}`}
            >
              <span className="truncate">
                {task.parent_task_summary.ticket_display}
              </span>
            </button>
          }
        />
      )}
      {(task.child_tasks_summary?.children ?? []).map((child) => (
        <PocketRow
          key={child.id}
          testId={`task-pocket-row-child-${child.id}`}
          leading={<Icons.ArrowDown className={ROW_ICON} aria-hidden="true" />}
          title={t("subtask")}
          trailing={
            <button
              type="button"
              onClick={() => onOpenTask(child.id)}
              title={`${child.title} (${tBoard(TASK_STATUS_LABEL_KEYS[child.status])})`}
              className={LINK_VALUE}
              data-testid={`task-pocket-child-${child.id}`}
            >
              <span className="truncate">{child.ticket_display}</span>
            </button>
          }
        />
      ))}
      {pullRequestUrl ? (
        <PocketRow
          testId="task-pocket-row-pull-request"
          leading={
            <Icons.GitPullRequest className={ROW_ICON} aria-hidden="true" />
          }
          title={t("pullRequest")}
          trailing={
            <button
              type="button"
              onClick={() => void openExternalUrl(pullRequestUrl)}
              className={LINK_VALUE}
              data-testid="task-pocket-pull-request"
            >
              <span className="truncate">
                {taskPullRequestText(task) ?? t("open")}
              </span>
              <Icons.ArrowUpRight
                className="h-3 w-3 shrink-0"
                aria-hidden="true"
              />
            </button>
          }
        />
      ) : null}
    </div>
  );
}
